import { createContext, useContext, useState, useEffect } from 'react';
import api from './api';

const AuthContext = createContext(null);

const TOKEN_KEY = 'emr_auth_token';
const USER_KEY = 'emr_user';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check for existing session on mount
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const savedUser = localStorage.getItem(USER_KEY);
      
      if (token && savedUser) {
        // Verify token is still valid by calling /auth/me
        try {
          const response = await api.get('/auth/me');
          setUser(response.data);
          localStorage.setItem(USER_KEY, JSON.stringify(response.data));
        } catch (err) {
          // Token invalid or expired, clear storage
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          setUser(null);
        }
      }
    } catch (err) {
      console.error('Auth check failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await api.post('/auth/login', { email, password });
      
      const { token, user: userData } = response.data;
      
      // Store token and user
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));
      
      setUser(userData);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    
    const rolePermissions = {
      admin: ['*'],
      physician: ['patients:*', 'forms:*', 'submissions:*', 'reports:read', 'users:read', 'invoices:*', 'clinical_notes:*', 'appointments:*'],
      nurse: ['patients:read', 'patients:update', 'forms:read', 'submissions:create', 'submissions:read', 'clinical_notes:read', 'appointments:read', 'appointments:create', 'appointments:update'],
      clerk: ['patients:read', 'patients:create', 'forms:read', 'submissions:create', 'submissions:read', 'invoices:read', 'appointments:read', 'appointments:create'],
      readonly: ['patients:read', 'forms:read', 'submissions:read', 'appointments:read'],
    };

    const perms = rolePermissions[user.role] || [];
    
    if (perms.includes('*')) return true;
    if (perms.includes(permission)) return true;
    
    // Check wildcard
    const [resource] = permission.split(':');
    if (perms.includes(`${resource}:*`)) return true;
    
    return false;
  };

  // Helper to get token for API calls
  const getToken = () => localStorage.getItem(TOKEN_KEY);

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    hasPermission,
    isAuthenticated: !!user,
    getToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
